import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MyPropertiesResponse,
  OwnedPortalProperty,
  PortalPropertyUnit,
  PortalService,
  RentedPortalProperty,
} from '../../services/portal.service';
import { LanguageService } from '../../../../../core/services/language.service';
import { TranslatePipe } from '../../../../../core/pipes/translate.pipe';

@Component({
  selector: 'app-my-properties',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './my-properties.html',
  styleUrl: './my-properties.scss',
})
export class MyProperties implements OnInit {
  private portalService = inject(PortalService);
  private languageService = inject(LanguageService);

  owned: OwnedPortalProperty[] = [];
  rented: RentedPortalProperty[] = [];
  summary: MyPropertiesResponse['summary'] = {
    totalOwned: 0,
    totalRented: 0,
  };

  loading = true;
  error: string | null = null;

  ngOnInit(): void {
    this.loadProperties();
  }

  loadProperties(): void {
    this.loading = true;
    this.error = null;

    this.portalService.getMyProperties().subscribe({
      next: (res) => {
        this.owned = res.owned || [];
        this.rented = res.rented || [];
        this.summary = res.summary || {
          totalOwned: this.owned.length,
          totalRented: this.rented.length,
        };
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || err.message || 'Failed to load properties';
        this.loading = false;
      },
    });
  }

  get currentLanguage(): string {
    return this.languageService.getCurrentLanguage();
  }

  get totalProperties(): number {
    return (this.summary.totalOwned || 0) + (this.summary.totalRented || 0);
  }

  unitTitle(unit: PortalPropertyUnit | null | undefined): string {
    if (!unit) return 'seekerDashboard.myProperties.unitUnavailable';
    const primary = this.currentLanguage === 'ar' ? unit.title_ar : unit.title_en;
    const fallback = this.currentLanguage === 'ar' ? unit.title_en : unit.title_ar;
    return primary || fallback || 'seekerDashboard.myProperties.untitledUnit';
  }

  unitLocation(unit: PortalPropertyUnit | null | undefined): string {
    if (!unit?.location) return 'seekerDashboard.myProperties.locationUnavailable';

    const parts = [unit.location.city, unit.location.area, unit.location.address]
      .map((part) => this.localized(part))
      .filter(Boolean);

    return parts.length ? parts.join(', ') : 'seekerDashboard.myProperties.locationUnavailable';
  }

  unitImage(unit: PortalPropertyUnit | null | undefined): string | null {
    return unit?.gallery?.find((image) => !!image.secure_url)?.secure_url || null;
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '-';
    const locale = this.currentLanguage === 'ar' ? 'ar-EG' : 'en-US';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(value));
  }

  statusClass(status: string | null | undefined): string {
    switch ((status || '').toLowerCase()) {
      case 'completed':
      case 'active':
        return 'badge-success';
      case 'draft':
      case 'pending':
        return 'badge-warning';
      case 'terminated':
      case 'terminated_early':
      case 'cancelled':
        return 'badge-danger';
      default:
        return 'badge-neutral';
    }
  }

  translationValue(value: string | null | undefined, prefix: string): string {
    if (!value) return 'seekerDashboard.myProperties.notAvailable';
    return `${prefix}.${value}`;
  }

  isTranslationKey(value: string): boolean {
    return value.startsWith('seekerDashboard.');
  }

  private localized(value: { ar?: string; en?: string } | undefined): string {
    if (!value) return '';
    const primary = this.currentLanguage === 'ar' ? value.ar : value.en;
    const fallback = this.currentLanguage === 'ar' ? value.en : value.ar;
    return (primary || fallback || '').trim();
  }
}
